import React, { useState, useEffect } from "react";
import {
  Search,
  Plus,
  MoreVertical,
  Users,
  UserCheck,
  UserX,
  Clock,
  Edit2,
  Trash2,
  Eye,
  Power,
  CheckCircle,
  XCircle,
  AlertCircle,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import { useNavigate } from "react-router-dom";
import "../../../assets/css/admin/SubAdmin.css";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";

const MOCK_SUB_ADMINS = [
  {
    id: 1,
    name: "Sarah Johnson",
    email: "sarah.j@edtech.com",
    avatar: null,
    role: "Super Admin",
    status: "Active",
    lastActive: "2024-01-28T10:30:00",
    createdAt: "2024-01-15T09:00:00",
  },
  {
    id: 2,
    name: "Michael Chen",
    email: "michael.c@edtech.com",
    avatar: null,
    role: "Moderator",
    status: "Active",
    lastActive: "2024-01-28T14:20:00",
    createdAt: "2024-01-10T11:00:00",
  },
  {
    id: 3,
    name: "Emma Rodriguez",
    email: "emma.r@edtech.com",
    avatar: null,
    role: "Moderator",
    status: "Suspended",
    lastActive: "2024-01-25T16:45:00",
    createdAt: "2023-12-20T10:00:00",
  },
  {
    id: 4,
    name: "David Park",
    email: "david.p@edtech.com",
    avatar: null,
    role: "Super Admin",
    status: "Active",
    lastActive: "2024-01-28T13:15:00",
    createdAt: "2024-01-05T14:30:00",
  },
  {
    id: 5,
    name: "Jessica Williams",
    email: "jessica.w@edtech.com",
    avatar: null,
    role: "Moderator",
    status: "Pending",
    lastActive: null,
    createdAt: "2024-01-25T09:00:00",
  },
  {
    id: 6,
    name: "James Miller",
    email: "james.m@edtech.com",
    avatar: null,
    role: "Moderator",
    status: "Active",
    lastActive: "2024-01-27T11:30:00",
    createdAt: "2024-01-12T10:00:00",
  },
];

const samGetInitials = (name) =>
  name
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase();

const samFormatDate = (dateString) => {
  if (!dateString) return "Never";
  const date = new Date(dateString);
  const diffMins = Math.floor((new Date() - date) / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffMins < 1440) return `${Math.floor(diffMins / 60)}h ago`;
  if (diffMins < 10080) return `${Math.floor(diffMins / 1440)}d ago`;
  return date.toLocaleDateString();
};

const SubAdminManagement = () => {
  const navigate = useNavigate();

  const [subAdmins, setSubAdmins] = useState(MOCK_SUB_ADMINS);
  const [searchQuery, setSearchQuery] = useState("");
  const [openDropdown, setOpenDropdown] = useState(null);
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    admin: null,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({
    key: "name",
    direction: "asc",
  });

  const itemsPerPage = 5;

  /* ── Filter ── */
  const filteredAdmins = subAdmins.filter((admin) => {
    const q = searchQuery.toLowerCase();
    return (
      admin.name.toLowerCase().includes(q) ||
      admin.email.toLowerCase().includes(q) ||
      admin.role.toLowerCase().includes(q)
    );
  });

  /* ── Sort ── */
  const sortedAdmins = [...filteredAdmins].sort((a, b) => {
    if (sortConfig.key === "lastActive") {
      const aT = a.lastActive ? new Date(a.lastActive).getTime() : 0;
      const bT = b.lastActive ? new Date(b.lastActive).getTime() : 0;
      return sortConfig.direction === "asc" ? aT - bT : bT - aT;
    }
    const aV = a[sortConfig.key]?.toLowerCase() || "";
    const bV = b[sortConfig.key]?.toLowerCase() || "";
    if (aV < bV) return sortConfig.direction === "asc" ? -1 : 1;
    if (aV > bV) return sortConfig.direction === "asc" ? 1 : -1;
    return 0;
  });

  /* ── Pagination ── */
  const totalPages = Math.ceil(sortedAdmins.length / itemsPerPage);
  const paginatedAdmins = sortedAdmins.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  /* ── Stats ── */
  const stats = {
    total: subAdmins.length,
    active: subAdmins.filter((a) => a.status === "Active").length,
    pending: subAdmins.filter((a) => a.status === "Pending").length,
    suspended: subAdmins.filter((a) => a.status === "Suspended").length,
  };

  /* ── Handlers ── */
  const samHandleSort = (key) =>
    setSortConfig({
      key,
      direction:
        sortConfig.key === key && sortConfig.direction === "asc"
          ? "desc"
          : "asc",
    });

  const samHandleDelete = (admin) => {
    setDeleteModal({ isOpen: true, admin });
    setOpenDropdown(null);
  };

  const samConfirmDelete = () => {
    setSubAdmins(subAdmins.filter((a) => a.id !== deleteModal.admin.id));
    setDeleteModal({ isOpen: false, admin: null });
  };

  const samHandleStatusToggle = (admin) => {
    setSubAdmins(
      subAdmins.map((a) =>
        a.id === admin.id
          ? { ...a, status: a.status === "Active" ? "Suspended" : "Active" }
          : a,
      ),
    );
    setOpenDropdown(null);
  };

  /* ── Status Badge ── */
  const samGetStatusBadge = (status) => {
    const icons = {
      Active: <CheckCircle size={14} />,
      Pending: <Clock size={14} />,
      Suspended: <XCircle size={14} />,
    };
    return (
      <span className={`sam-status-badge sam-status-${status.toLowerCase()}`}>
        {icons[status]}
        {status}
      </span>
    );
  };

  /* ── Close dropdown on outside click ── */
  useEffect(() => {
    const handler = (e) => {
      if (!e.target.closest(".sam-dropdown-trigger")) setOpenDropdown(null);
    };
    document.addEventListener("click", handler);
    return () => document.removeEventListener("click", handler);
  }, []);

  const statCards = [
    {
      icon: Users,
      label: "Total Sub Admins",
      value: stats.total,
      color: "garnet",
    },
    {
      icon: UserCheck,
      label: "Active Sub Admins",
      value: stats.active,
      color: "green",
    },
    {
      icon: Clock,
      label: "Pending Invitations",
      value: stats.pending,
      color: "amber",
    },
    {
      icon: UserX,
      label: "Suspended Accounts",
      value: stats.suspended,
      color: "red",
    },
  ];

  return (
    <AdminLayout>
      <div className="sam-container">
        {/* ════ Page Header ════ */}
        <div className="sam-header">
          <div className="sam-header-content">
            <div className="sam-header-inner">
              <div className="sam-header-text">
                <h1 className="sam-title">Sub Admin Management</h1>
                <p className="sam-subtitle">
                  Manage administrative access and permissions
                </p>
              </div>

              <div className="sam-header-actions">
                {/* Search */}
                <div className="sam-search-wrapper">
                  <Search size={20} className="sam-search-icon" />
                  <input
                    type="text"
                    placeholder="Search sub admins..."
                    value={searchQuery}
                    onChange={(e) => {
                      setSearchQuery(e.target.value);
                      setCurrentPage(1);
                    }}
                    className="sam-search-input"
                  />
                </div>

                <button
                  className="sam-btn-primary"
                  onClick={() => navigate("/admin/sub-admins/create")}
                >
                  <Plus size={20} />
                  Add New Admin
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ════ Main Content ════ */}
        <div className="sam-main-content">
          {/* ── KPI Stat Cards — Dashboard Style ── */}
          <div className="sam-stats-grid">
            {statCards.map((stat, i) => {
              const Icon = stat.icon;
              return (
                <div key={i} className="sam-stat-card">
                  <div className="sam-stat-card-top">
                    <div
                      className={`sam-stat-icon sam-stat-icon-${stat.color}`}
                    >
                      <Icon size={22} />
                    </div>
                  </div>
                  <div className="sam-stat-info">
                    <p className="sam-stat-value">{stat.value}</p>
                    <p className="sam-stat-label">{stat.label}</p>
                  </div>
                </div>
              );
            })}
          </div>

          {/* ── Table Section ── */}
          <div className="sam-table-container">
            {/* Table header bar */}
            <div className="sam-table-header">
              <h2 className="sam-table-title">
                Sub Administrators ({filteredAdmins.length})
              </h2>
              <div className="sam-table-count">
                Showing {paginatedAdmins.length} of {filteredAdmins.length}{" "}
                results
              </div>
            </div>

            {/* Table */}
            <div className="sam-table-wrapper">
              <table className="sam-table">
                <thead>
                  <tr>
                    {[
                      { key: "id", label: "#ID", sortable: false },
                      { key: "name", label: "Admin", sortable: true },
                      { key: "role", label: "Role", sortable: true },
                      { key: "status", label: "Status", sortable: true },
                      {
                        key: "lastActive",
                        label: "Last Active",
                        sortable: true,
                      },
                      { key: "actions", label: "Actions", sortable: false },
                    ].map((col) => (
                      <th
                        key={col.key}
                        onClick={() => col.sortable && samHandleSort(col.key)}
                        className={col.sortable ? "sam-sortable" : ""}
                      >
                        <div className="sam-th-content">
                          {col.label}
                          {col.sortable && sortConfig.key === col.key && (
                            <span className="sam-sort-indicator">
                              {sortConfig.direction === "asc" ? "↑" : "↓"}
                            </span>
                          )}
                        </div>
                      </th>
                    ))}
                  </tr>
                </thead>

                <tbody>
                  {paginatedAdmins.map((admin) => (
                    <tr key={admin.id}>
                      {/* ── ID Cell ── */}
                      <td>
                        <span className="sam-table-id">{admin.id}</span>
                      </td>

                      {/* ── Admin Info ── */}
                      <td>
                        <div className="sam-admin-cell">
                          <div className="sam-avatar">
                            {samGetInitials(admin.name)}
                          </div>
                          <div className="sam-admin-info">
                            <div className="sam-admin-name">{admin.name}</div>
                            <div className="sam-admin-email">{admin.email}</div>
                          </div>
                        </div>
                      </td>

                      {/* ── Role ── */}
                      <td>
                        <span className="sam-role">{admin.role}</span>
                      </td>

                      {/* ── Status ── */}
                      <td>{samGetStatusBadge(admin.status)}</td>

                      {/* ── Last Active ── */}
                      <td>
                        <span className="sam-last-active">
                          {samFormatDate(admin.lastActive)}
                        </span>
                      </td>

                      {/* ── Actions ── */}
                      <td className="sam-actions-cell">
                        <button
                          className="sam-dropdown-trigger sam-action-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setOpenDropdown(
                              openDropdown === admin.id ? null : admin.id,
                            );
                          }}
                        >
                          <MoreVertical size={18} />
                        </button>

                        {openDropdown === admin.id && (
                          <div className="sam-dropdown">
                            <button
                              className="sam-dropdown-item"
                              onClick={() => console.log("View", admin)}
                            >
                              <Eye size={16} /> View Details
                            </button>
                            <button
                              className="sam-dropdown-item"
                              onClick={() => console.log("Edit", admin)}
                            >
                              <Edit2 size={16} /> Edit Admin
                            </button>
                            <button
                              className={`sam-dropdown-item ${
                                admin.status === "Active"
                                  ? "sam-warning"
                                  : "sam-success"
                              }`}
                              onClick={() => samHandleStatusToggle(admin)}
                            >
                              <Power size={16} />
                              {admin.status === "Active"
                                ? "Suspend"
                                : "Activate"}
                            </button>
                            <div className="sam-dropdown-divider" />
                            <button
                              className="sam-dropdown-item sam-danger"
                              onClick={() => samHandleDelete(admin)}
                            >
                              <Trash2 size={16} /> Delete
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  ))}

                  {/* Empty state */}
                  {paginatedAdmins.length === 0 && (
                    <tr>
                      <td colSpan={6} className="sam-empty-state">
                        <Users size={40} />
                        <p>No sub admins found</p>
                        <span>Try adjusting your search query</span>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="sam-pagination">
              <div className="sam-pagination-info">
                Page {currentPage} of {totalPages || 1}
              </div>
              <div className="sam-pagination-buttons">
                <button
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                  className="sam-pagination-btn"
                >
                  <ChevronLeft size={16} /> Previous
                </button>
                <button
                  onClick={() =>
                    setCurrentPage((p) => Math.min(totalPages, p + 1))
                  }
                  disabled={currentPage === totalPages || totalPages === 0}
                  className="sam-pagination-btn"
                >
                  Next <ChevronRight size={16} />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* ════ Delete Confirmation Modal ════ */}
        {deleteModal.isOpen && (
          <div
            className="sam-modal-backdrop"
            onClick={() => setDeleteModal({ isOpen: false, admin: null })}
          >
            <div className="sam-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sam-modal-icon">
                <AlertCircle size={28} />
              </div>
              <h3 className="sam-modal-title">Delete Sub Admin?</h3>
              <p className="sam-modal-text">
                Are you sure you want to delete{" "}
                <strong>{deleteModal.admin?.name}</strong>? This action cannot
                be undone and will permanently remove their access.
              </p>
              <div className="sam-modal-actions">
                <button
                  onClick={() => setDeleteModal({ isOpen: false, admin: null })}
                  className="sam-btn-secondary"
                >
                  Cancel
                </button>
                <button onClick={samConfirmDelete} className="sam-btn-danger">
                  Delete Admin
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default SubAdminManagement;
